import React, { useState, useEffect } from 'react';
import { logger } from '../utils/logger'
import {
  Bot as BotIcon, 
  X, 
  Eye, 
  MessageSquare, 
  UserCheck, 
  Save,
  RefreshCw
} from 'lucide-react';
import { userBotPermissionService, BotPermission, Bot, UpdatePermissionsRequest } from '../services/userBotPermissionService';

interface UserBotPermissionsProps {
  userId: number;
  userName: string;
  onClose: () => void;
  onSave: () => void;
}

const UserBotPermissions: React.FC<UserBotPermissionsProps> = ({ 
  userId, 
  userName, 
  onClose, 
  onSave 
}) => {
  const [bots, setBots] = useState<Bot[]>([]);
  const [permissions, setPermissions] = useState<BotPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [botsData, permissionsData] = await Promise.all([
        userBotPermissionService.getAvailableBots(userId),
        userBotPermissionService.getUserPermissions(userId)
      ]);

      setBots(botsData);
      setPermissions(permissionsData);
    } catch (err: any) {
      logger.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getPermissionForBot = (botId: number): BotPermission | undefined => {
    return permissions.find(p => p.bot_session_id === botId);
  };

  const updatePermission = (botId: number, field: 'can_respond' | 'can_view' | 'can_assign', value: boolean) => {
    setPermissions(prev => {
      const existing = prev.find(p => p.bot_session_id === botId);
      if (existing) {
        return prev.map(p => 
          p.bot_session_id === botId 
            ? { ...p, [field]: value }
            : p
        );
      } else {
        return [...prev, {
          user_id: userId,
          bot_session_id: botId,
          admin_id: 0, // Será definido pelo backend
          can_respond: field === 'can_respond' ? value : false,
          can_view: field === 'can_view' ? value : false,
          can_assign: field === 'can_assign' ? value : false
        }];
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const updateData: UpdatePermissionsRequest = {
        permissions: bots.map(bot => {
          const permission = getPermissionForBot(bot.id);
          return {
            bot_session_id: bot.id,
            can_respond: permission?.can_respond || false,
            can_view: permission?.can_view || false,
            can_assign: permission?.can_assign || false
          };
        })
      };

      await userBotPermissionService.updateUserPermissions(userId, updateData);
      onSave();
    } catch (err: any) {
      logger.error('Erro ao salvar permissões:', err);
      setError(err.response?.data?.message || 'Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            <span>Carregando permissões...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Permissões de Bots - {userName}
            </h2>
            <p className="text-gray-600 mt-1">
              Defina quais bots este atendente pode responder
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {bots.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BotIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nenhum bot encontrado</p>
            </div>
          ) : (
            bots.map(bot => {
              const permission = getPermissionForBot(bot.id);
              return (
                <div key={bot.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                        <BotIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{bot.session_name}</h3>
                        <p className="text-sm text-gray-500">{bot.phone_number}</p>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          bot.status === 'connected' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {bot.status === 'connected' ? 'Conectado' : 'Desconectado'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Pode Responder */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <MessageSquare className="h-5 w-5 text-blue-600 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Pode Responder</span>
                      </div>
                      <button
                        onClick={() => updatePermission(bot.id, 'can_respond', !permission?.can_respond)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          permission?.can_respond ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permission?.can_respond ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Pode Visualizar */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <Eye className="h-5 w-5 text-green-600 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Pode Visualizar</span>
                      </div>
                      <button
                        onClick={() => updatePermission(bot.id, 'can_view', !permission?.can_view)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          permission?.can_view ? 'bg-green-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permission?.can_view ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Pode Atribuir */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <UserCheck className="h-5 w-5 text-purple-600 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Pode Atribuir</span>
                      </div>
                      <button
                        onClick={() => updatePermission(bot.id, 'can_assign', !permission?.can_assign)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          permission?.can_assign ? 'bg-purple-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permission?.can_assign ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Salvar Permissões
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserBotPermissions;
